import { showToast } from '@bp/ui-components';
import i18next from 'i18next';
import { action, makeAutoObservable } from 'mobx';
import { TimeGridEntryType } from '../../components/TimeGrid/graphql/types';
import { CardType } from '../../components/TimetableVersion/graphql/types';
import { GridType } from '../../pages/Timetable/Plan/TimetableVersion/Board/types';
import { timeAvailabilityStoreHandler } from '../../pages/Timetable/Plan/TimetableVersion/TimetableVersion';
import { _ClassesQuery, _PeopleQuery, _RoomsQuery, _SubjectsQuery } from '../../types/planung-graphql-client-defs';
import {
  type ParsedTimefoldAnalysis,
  parseTimefoldScoreAnalysis,
} from '../../utils/timetable/parseTimefoldScoreAnalysis';
import {
  analyzeTimefoldTimetableVersion,
  controlTimetableVersion,
  downloadAscExport,
  testTimetableVersion,
} from '../../utils/timetable/timetableVersion';
import { SolverStore } from '../SolverStore';
import type { UserConfigStore } from '../userConfig/UserConfigStore';
import { CardStore } from './CardStore';
import { ConflictStore } from './ConflictStore';
import {
  BoardLayout,
  CardEditModalType,
  CardStack,
  CellItem,
  CurrentVersionType,
  DayItem,
  HighlightType,
  HourItem,
  Item,
  PickedCardType,
  RoomItem,
  RowItem,
  Warnings,
} from './PinboardStoreTypes';
import { PinboardUIStore, PreviewMapContext } from './PinboardUIStore';
import {
  AvailabilityCardType,
  AvailabilityResourceType,
  CardAvailability,
  Day,
  TimeAvailabilityStore,
} from './TimeAvailabilityStore';

export type { AvailabilityResourceType, AvailabilityCardType, CardAvailability };

export class PinboardStore {
  public readonly cardStore: CardStore;
  public readonly conflictStore: ConflictStore;
  public readonly uiStore: PinboardUIStore;

  // Own state
  public currentVersion: CurrentVersionType | null = null;
  private schoolyear: string | null = null;
  public readonly: boolean = false;
  public temporarilyEditable: boolean = false;
  public timetableActive = false;
  public timeGridEntries: TimeGridEntryType[] | null = null;

  private _hours: HourItem[] = [];
  private _days: DayItem[] = [];
  private _timeAvailabilityStore: TimeAvailabilityStore | null = null;
  public solverStore: SolverStore;

  constructor({
    currentVersion,
    timeGridEntries,
    days,
    readonly,
    timetableActive,
    temporarilyEditable,
    schoolyear,
    userConfig = null,
  }: {
    currentVersion: CurrentVersionType;
    timeGridEntries: TimeGridEntryType[];
    days: DayItem[];
    readonly: boolean;
    timetableActive: boolean;
    temporarilyEditable: boolean;
    schoolyear: string | null;
    /**
     * Quelle und Ziel der gespeicherten Ansichtseinstellungen. Wird bewusst
     * hereingereicht statt importiert (kein Zirkelbezug, und Tests kommen ohne
     * UserConfig aus — dann bleibt alles im Speicher).
     */
    userConfig?: UserConfigStore | null;
  }) {
    this.conflictStore = new ConflictStore(this);
    this.cardStore = new CardStore(this);
    this.uiStore = new PinboardUIStore(this, userConfig);

    this.schoolyear = schoolyear;
    timeGridEntries.forEach((entry) => {
      days.forEach((day) => {
        this.cardStore.placedCards.set(`${entry.uuid}:${day.value}`, new Map());
      });
      this._days = days;
      this._hours.push({ label: entry.shortName, value: entry.uuid, isPause: entry.pause });
    });
    this.timeGridEntries = timeGridEntries;
    this.currentVersion = currentVersion;

    this.readonly = readonly;
    this.timetableActive = timetableActive;
    this.temporarilyEditable = temporarilyEditable;

    this._timeAvailabilityStore = timeAvailabilityStoreHandler.get(currentVersion.uuid);

    this.solverStore = new SolverStore(userConfig);
    this.solverStore.setOnJobCompleted(async () => {
      this.reloadVersionData();
    });

    makeAutoObservable(this, {
      cardStore: false,
      conflictStore: false,
      uiStore: false,
      setCardEditModal: action.bound,
      setTimefoldAnalyzeModal: action.bound,
      removeOtherRooms: action.bound,
      setRoomsForCards: action.bound,
      getTimeAvailabilityStore: action.bound,
    });
  }

  getTimeAvailabilityStore() {
    return this._timeAvailabilityStore;
  }

  setTemporarilyEditable(value: boolean) {
    this.temporarilyEditable = value;
    this.readonly = !value;
  }

  /**
   * Single source of truth for "may the user mutate the board right now?". False when
   * the board is read-only OR a solver job is running — while the solver owns the plan,
   * all destructive operations (generate, test, lock/unlock, move/delete cards, …) are
   * blocked so the user can't fight the running solver.
   */
  get isEditable(): boolean {
    return !this.readonly && !this.solverStore.jobRunning;
  }

  get hours(): HourItem[] {
    return this._hours;
  }

  set hours(value: HourItem[]) {
    this._hours = value;
  }

  get days(): DayItem[] {
    return this._days;
  }

  set days(value: DayItem[]) {
    this._days = value;
  }

  // ─── Delegations to cardStore (observable state) ──────────────────────────

  get minimalCards() {
    return this.cardStore.minimalCards;
  }
  get placedCards() {
    return this.cardStore.placedCards;
  }
  get cardsBySlotAndResource() {
    return this.cardStore.cardsBySlotAndResource;
  }
  get placedCardsDay() {
    return this.cardStore.placedCardsDay;
  }
  get placedCardsTimeGridEntries() {
    return this.cardStore.placedCardsTimeGridEntries;
  }
  get placedCardsSet() {
    return this.cardStore.placedCardsSet;
  }
  get freeCardsSet() {
    return this.cardStore.freeCardsSet;
  }
  get defaultCardRooms() {
    return this.cardStore.defaultCardRooms;
  }
  get cardRooms() {
    return this.cardStore.cardRooms;
  }
  get cardClasses() {
    return this.cardStore.cardClasses;
  }
  get cardTeacher() {
    return this.cardStore.cardTeacher;
  }
  get lockedCards() {
    return this.cardStore.lockedCards;
  }
  get teachers() {
    return this.cardStore.teachers;
  }
  get classes() {
    return this.cardStore.classes;
  }
  get subjects() {
    return this.cardStore.subjects;
  }
  get rooms() {
    return this.cardStore.rooms;
  }
  get gridCards() {
    return this.cardStore.gridCards;
  }
  get subjectContainerSlots() {
    return this.cardStore.subjectContainerSlots;
  }

  // ─── Delegations to conflictStore ─────────────────────────────────────────

  get cardConflicts() {
    return this.conflictStore.cardConflicts;
  }

  // ─── Delegations to uiStore ───────────────────────────────────────────────

  get pickedCard() {
    return this.uiStore.pickedCard;
  }
  get pinnedCard() {
    return this.uiStore.pinnedCard;
  }
  set pinnedCard(value: string | null) {
    this.uiStore.pinnedCard = value;
  }
  get hoveredCard() {
    return this.uiStore.hoveredCard;
  }
  get selectedRows() {
    return this.uiStore.selectedRows;
  }
  get hiddenRows() {
    return this.uiStore.hiddenRows;
  }
  get selectedColumns() {
    return this.uiStore.selectedColumns;
  }
  get rowsToHighlight() {
    return this.uiStore.rowsToHighlight;
  }
  get context() {
    return this.uiStore.context;
  }
  get highlightMode() {
    return this.uiStore.highlightMode;
  }
  get conflictModal() {
    return this.uiStore.conflictModal;
  }
  get conflictListModal() {
    return this.uiStore.conflictListModal;
  }
  get constraintModal() {
    return this.uiStore.constraintModal;
  }
  get testModal() {
    return this.uiStore.testModal;
  }
  get timefoldAnalyzeModal() {
    return this.uiStore.timefoldAnalyzeModal;
  }
  get cardEditModal() {
    return this.uiStore.cardEditModal;
  }
  get controlMode() {
    return this.uiStore.controlMode;
  }
  get findingHighlights() {
    return this.uiStore.findingHighlights;
  }
  get timefoldAnalyzeMode() {
    return this.uiStore.timefoldAnalyzeMode;
  }
  getFindingSlotHint(rowUuid: string, hourUuid: string, day: string) {
    return this.uiStore.getFindingSlotHint(rowUuid, hourUuid, day);
  }
  addFindingSlotHighlights(slots: import('./PinboardStoreTypes').FindingSlotHighlight[]) {
    return this.uiStore.addFindingSlotHighlights(slots);
  }
  removeFindingSlotHighlights(slots: import('./PinboardStoreTypes').FindingSlotHighlight[]) {
    return this.uiStore.removeFindingSlotHighlights(slots);
  }
  clearFindingSlotHighlights() {
    return this.uiStore.clearFindingSlotHighlights();
  }
  get isFullscreen() {
    return this.uiStore.isFullscreen;
  }
  get rowHeight() {
    return this.uiStore.rowHeight;
  }
  get boardLayout() {
    return this.uiStore.boardLayout;
  }
  get boardTopHeight() {
    return this.uiStore.boardTopHeight;
  }
  get boardSplitWidth() {
    return this.uiStore.boardSplitWidth;
  }
  get boardPickerWidth() {
    return this.uiStore.boardPickerWidth;
  }
  get boardPickerHeight() {
    return this.uiStore.boardPickerHeight;
  }
  get showPreviewMap() {
    return this.uiStore.showPreviewMap;
  }
  get previewMapContext() {
    return this.uiStore.previewMapContext;
  }
  get previewMapAuto() {
    return this.uiStore.previewMapAuto;
  }
  get boardPreviewMapWidth() {
    return this.uiStore.boardPreviewMapWidth;
  }
  get boardPreviewMapHeight() {
    return this.uiStore.boardPreviewMapHeight;
  }
  get showCardInfo() {
    return this.uiStore.showCardInfo;
  }
  get showTimetablePreview() {
    return this.uiStore.showTimetablePreview;
  }
  get boardTimetablePreviewTeachersWidth() {
    return this.uiStore.boardTimetablePreviewTeachersWidth;
  }
  get boardTimetablePreviewTeachersHeight() {
    return this.uiStore.boardTimetablePreviewTeachersHeight;
  }
  get boardTimetablePreviewRoomsWidth() {
    return this.uiStore.boardTimetablePreviewRoomsWidth;
  }
  get boardTimetablePreviewRoomsHeight() {
    return this.uiStore.boardTimetablePreviewRoomsHeight;
  }
  get timetablePreviewCellMinHeight() {
    return this.uiStore.timetablePreviewCellMinHeight;
  }
  get cardColorBySubject() {
    return this.uiStore.cardColorBySubject;
  }
  get showTeacherInsteadOfSubject() {
    return this.uiStore.showTeacherInsteadOfSubject;
  }
  setShowTeacherInsteadOfSubject(value: boolean) {
    return this.uiStore.setShowTeacherInsteadOfSubject(value);
  }
  get filterFunctionClasses() {
    return this.uiStore.filterFunctionClasses;
  }
  get groupRoomsByBuilding() {
    return this.uiStore.groupRoomsByBuilding;
  }
  get cardSearchString() {
    return this.uiStore.cardSearchString;
  }
  get showPause() {
    return this.uiStore.showPause;
  }
  get columns() {
    return this.uiStore.columns;
  }
  get showWarningsHighlight() {
    return this.uiStore.showWarningsHighlight;
  }
  get showRoomsHighlight() {
    return this.uiStore.showRoomsHighlight;
  }
  get showConflictsHighlight() {
    return this.uiStore.showConflictsHighlight;
  }
  get showEpochRanges() {
    return this.uiStore.showEpochRanges;
  }
  setShowEpochRanges(value: boolean) {
    return this.uiStore.setShowEpochRanges(value);
  }

  // ─── Method delegations to cardStore ──────────────────────────────────────

  private _reloadVersionData: (() => void) | null = null;

  /** Vom TimetableProvider registriert: löst ein network-only Re-Execute der TimetableVersionData-Query aus. */
  setReloadVersionData(callback: () => void) {
    this._reloadVersionData = callback;
  }

  /** Board-Cards frisch aus der bestehenden TimetableVersionData-Query nachladen (Solver-Ende, Lesson-Delete …). */
  reloadVersionData() {
    this._reloadVersionData?.();
  }

  initCards(cards: CardType[]) {
    return this.cardStore.initCards(cards);
  }
  initData(params: {
    cards: CardType[];
    teachers: Pick<_PeopleQuery, 'people'>['people'];
    classes: Pick<_ClassesQuery, 'classes'>['classes'];
    rooms: Pick<_RoomsQuery, 'rooms'>['rooms'];
    subjects: Pick<_SubjectsQuery, 'subjects'>['subjects'];
  }) {
    return this.cardStore.initData(params);
  }
  getCurrentVersion() {
    return this.currentVersion;
  }
  getRows() {
    return this.cardStore.getRows();
  }
  getCardByUuid(uuid: string): CardType | undefined {
    return this.cardStore.getCardByUuid(uuid);
  }
  /** Raw card without resolved rooms/weekday — for lightweight lookups (e.g. conflict checks) */
  getCardRaw(uuid: string): CardType | undefined {
    return this.cardStore.getCardRaw(uuid);
  }
  getCardsByLessonUuid(uuid: string): CardType[] | undefined {
    return this.cardStore.getCardsByLessonUuid(uuid);
  }
  getAvailableCardForCardUuid(cardUuid: string): AvailabilityCardType {
    return this.cardStore.getAvailableCardForCardUuid(cardUuid);
  }
  getPlacedCardsForCell(cell: CellItem): CardType[] {
    return this.cardStore.getPlacedCardsForCell(cell);
  }
  getPreviewCardsForCell(cell: CellItem): CardType[] {
    return this.cardStore.getPreviewCardsForCell(cell);
  }
  setResourcesPlaced(
    card: Pick<CardType, 'uuid' | 'teachers' | 'classes' | 'rooms' | 'lessonClasses' | 'subject'>,
    start: string,
    end: string,
    day: Day,
  ) {
    return this.cardStore.setResourcesPlaced(card, start, end, day);
  }
  setRoomsForCards(cards: CardType[], roomsToSet: RoomItem[], keepCurrent?: boolean) {
    return this.cardStore.setRoomsForCards(cards, roomsToSet, keepCurrent);
  }
  removeRoomsFromCards(
    cards: { uuid: string }[],
    roomsToRemove: (Partial<RoomItem> & { value: string })[],
    removeAll?: boolean,
  ) {
    return this.cardStore.removeRoomsFromCards(cards, roomsToRemove, removeAll);
  }
  getAllHoursBetween(start: string, end: string): HourItem[] {
    return this.cardStore.getAllHoursBetween(start, end);
  }
  getFollowers(hourUuid: string, duration: number): HourItem[] | null {
    return this.cardStore.getFollowers(hourUuid, duration);
  }
  lockCard(cardUuid: string) {
    return this.cardStore.lockCard(cardUuid);
  }
  unlockCard(cardUuid: string) {
    return this.cardStore.unlockCard(cardUuid);
  }
  getCardsAtDay(day: DayItem): CardType[] {
    return this.cardStore.getCardsAtDay(day);
  }
  getOverlappingCards(cardUuid: string): CardType[] {
    return this.cardStore.getOverlappingCards(cardUuid);
  }
  getCoexistLayout(cards: CardType[], day: Day, rowUuid: string) {
    return this.cardStore.getCoexistLayout(cards, day, rowUuid);
  }
  canBeDropped(cardUuid: string, cardDuration: number, hourUuid: string, day: Day): boolean {
    return this.cardStore.canBeDropped(cardUuid, cardDuration, hourUuid, day);
  }
  getCardsStack(): CardStack {
    return this.cardStore.getCardsStack();
  }
  getNameForUuid(uuid: string, type: AvailabilityResourceType): string {
    return this.cardStore.getNameForUuid(uuid, type);
  }
  addClasses(classes: Pick<_ClassesQuery, 'classes'>['classes']) {
    return this.cardStore.addClasses(classes);
  }
  removeClasses(uuids: string[]) {
    return this.cardStore.removeClasses(uuids);
  }
  getClassRows() {
    return this.cardStore.getClassRows();
  }
  addSubjects(subjects: Pick<_SubjectsQuery, 'subjects'>['subjects']) {
    return this.cardStore.addSubjects(subjects);
  }
  removeSubjects(uuids: string[]) {
    return this.cardStore.removeSubjects(uuids);
  }
  addTeachers(teachers: Pick<_PeopleQuery, 'people'>['people']) {
    return this.cardStore.addTeachers(teachers);
  }
  removeTeachers(uuids: string[]) {
    return this.cardStore.removeTeachers(uuids);
  }
  addRooms(rooms: Pick<_RoomsQuery, 'rooms'>['rooms']) {
    return this.cardStore.addRooms(rooms);
  }
  removeRooms(uuids: string[]) {
    return this.cardStore.removeRooms(uuids);
  }

  // ─── Method delegations to conflictStore ──────────────────────────────────

  setConflicts() {
    return this.conflictStore.setConflicts();
  }
  updateConflictsForCards(cardUuids: Set<string>) {
    return this.conflictStore.updateConflictsForCards(cardUuids);
  }
  removeConflicts() {
    return this.conflictStore.removeConflicts();
  }

  // ─── Method delegations to uiStore ────────────────────────────────────────

  setIsFullscreen(isFullscreen: boolean) {
    return this.uiStore.setIsFullscreen(isFullscreen);
  }
  setCardColorBySubject(value: boolean) {
    return this.uiStore.setCardColorBySubject(value);
  }
  setCardSearchString(value: string) {
    return this.uiStore.setCardSearchString(value);
  }
  setFilterFunctionClasses(value: boolean) {
    return this.uiStore.setFilterFunctionClasses(value);
  }
  setGroupRoomsByBuilding(value: boolean) {
    return this.uiStore.setGroupRoomsByBuilding(value);
  }
  setContext(type: GridType) {
    return this.uiStore.setContext(type);
  }

  /** Bewusster Kontextwechsel (wird gespeichert), siehe `PinboardUIStore.selectContext`. */
  selectContext(type: GridType) {
    return this.uiStore.selectContext(type);
  }

  /** Stellt den zuletzt gewaehlten Kontext her; erst nach dem Laden der Karten aufrufen. */
  restorePersistedContext() {
    return this.uiStore.restorePersistedContext();
  }

  /** Beendet die Beobachter des Stores (wird beim Verwerfen des Stores aufgerufen). */
  dispose() {
    this.uiStore.dispose();
  }
  setConflictModalConflicts(conflicts: Warnings | null) {
    return this.uiStore.setConflictModalConflicts(conflicts);
  }
  setConflictListModalOpen(isOpen: boolean) {
    return this.uiStore.setConflictListModalOpen(isOpen);
  }
  setConstraintModalOpen(setOpen: boolean) {
    return this.uiStore.setConstraintModalOpen(setOpen);
  }
  async setTestModalOpen(setOpen: boolean) {
    return this.uiStore.setTestModalOpen(setOpen);
  }
  async setTestModalFromGenerator(fromGenerator: boolean) {
    return this.uiStore.setTestModalFromGenerator(fromGenerator);
  }
  async setTestModalLoading(isLoading: boolean) {
    return this.uiStore.setTestModalLoading(isLoading);
  }
  async setTestModalResult(result: import('../../utils/timetable/types').GenericWarnings) {
    return this.uiStore.setTestModalResult(result);
  }
  setTimefoldAnalyzeModal(patch: Partial<import('./PinboardStoreTypes').TimefoldAnalyzeModalType>) {
    return this.uiStore.setTimefoldAnalyzeModal(patch);
  }
  setCardEditModal(modal: CardEditModalType) {
    return this.uiStore.setCardEditModal(modal);
  }
  setControlModeActive(setActive: boolean) {
    return this.uiStore.setControlModeActive(setActive);
  }
  setControlModeLoading(isLoading: boolean) {
    return this.uiStore.setControlModeLoading(isLoading);
  }
  setControlModeResult(result: import('../../utils/timetable/types').ControlResult | null) {
    return this.uiStore.setControlModeResult(result);
  }
  expandControlResults(expand: boolean) {
    return this.uiStore.expandControlResults(expand);
  }
  addFindingHighlights(highlights: string[]) {
    return this.uiStore.addFindingHighlights(highlights);
  }
  removeFindingHighlights(highlights: string[]) {
    return this.uiStore.removeFindingHighlights(highlights);
  }
  get scrollToRows() {
    return this.uiStore.scrollToRows;
  }
  setScrollToRows(rowUuids: string[]) {
    return this.uiStore.setScrollToRows(rowUuids);
  }
  clearScrollToRows() {
    return this.uiStore.clearScrollToRows();
  }
  async setHighlightMode(mode: HighlightType) {
    return this.uiStore.setHighlightMode(mode);
  }
  setPinnedCard(cardUuid: string | null) {
    return this.uiStore.setPinnedCard(cardUuid);
  }
  setHoveredCard(cardUuid: string) {
    return this.uiStore.setHoveredCard(cardUuid);
  }
  getCardToShowOnInfo() {
    return this.uiStore.getCardToShowOnInfo();
  }
  placeCard(_cell: CellItem) {
    return this.uiStore.placeCard(_cell);
  }
  placeCardFromPreview(day: DayItem, hour: HourItem, room?: RoomItem) {
    return this.uiStore.placeCardFromPreview(day, hour, room);
  }
  isPreviewDroppable(cardUuid: string, duration: number, hourUuid: string, day: Day): boolean {
    return this.uiStore.isPreviewDroppable(cardUuid, duration, hourUuid, day);
  }
  showConflictModal(card: CardType) {
    return this.uiStore.showConflictModal(card);
  }
  toggleAllRowSelection() {
    return this.uiStore.toggleAllRowSelection();
  }
  rowClickHandler(e: React.MouseEvent<HTMLDivElement>, row: RowItem) {
    return this.uiStore.rowClickHandler(e, row);
  }
  selectRows(rows: RowItem[]) {
    return this.uiStore.selectRows(rows);
  }
  toggleRowsVisibility(rows: RowItem[]) {
    return this.uiStore.toggleRowsVisibility(rows);
  }
  showAllRows() {
    return this.uiStore.showAllRows();
  }
  lockCardsAtDay(day: DayItem) {
    return this.uiStore.lockCardsAtDay(day);
  }
  unlockCardsAtDay(day: DayItem) {
    return this.uiStore.unlockCardsAtDay(day);
  }
  removeCardsInSelectedRows() {
    return this.uiStore.removeCardsInSelectedRows();
  }
  unlockCardsInSelectedRows() {
    return this.uiStore.unlockCardsInSelectedRows();
  }
  lockCardsInSelectedRows() {
    return this.uiStore.lockCardsInSelectedRows();
  }
  lowlightHour(hour: Item): boolean {
    return this.uiStore.lowlightHour(hour);
  }
  lowlightDay(day: DayItem): boolean {
    return this.uiStore.lowlightDay(day);
  }
  removeOtherRooms(conflict: Warnings[0], conflictIndex: number) {
    return this.uiStore.removeOtherRooms(conflict, conflictIndex);
  }
  public placeWithoutRoom(conflict: Warnings[0], conflictIndex: number) {
    return this.uiStore.placeWithoutRoom(conflict, conflictIndex);
  }
  public ignoreConflict(ignoreRoomWarnings?: boolean) {
    return this.uiStore.ignoreConflict(ignoreRoomWarnings);
  }
  public ignoreRoomConflict() {
    return this.uiStore.ignoreRoomConflict();
  }
  public abortConflict() {
    return this.uiStore.abortConflict();
  }
  public discardPickedCard() {
    return this.uiStore.discardPickedCard();
  }
  public discardPickedCardToOldPlacement() {
    return this.uiStore.discardPickedCardToOldPlacement();
  }
  public discardPickedCardToOldPlacementForRooms() {
    return this.uiStore.discardPickedCardToOldPlacementForRooms();
  }
  public discardPickedCardToRoomsStack() {
    return this.uiStore.discardPickedCardToRoomsStack();
  }
  public discardPickedCardToStack() {
    return this.uiStore.discardPickedCardToStack();
  }
  public pickCard(card: PickedCardType, cell?: CellItem, fromStack?: boolean, isCopy?: boolean) {
    return this.uiStore.pickCard(card, cell, fromStack, isCopy);
  }
  public pickRoomCard(card: PickedCardType, cell?: CellItem, fromStack?: boolean) {
    return this.uiStore.pickRoomCard(card, cell, fromStack);
  }
  getControlModeResult() {
    return this.uiStore.getControlModeResult();
  }
  setTimefoldParsedAnalysis(value: ParsedTimefoldAnalysis | null) {
    return this.uiStore.setTimefoldParsedAnalysis(value);
  }
  clearTimefoldParsedAnalysis() {
    return this.uiStore.clearTimefoldParsedAnalysis();
  }
  get timefoldParsedAnalysis() {
    return this.uiStore.timefoldParsedAnalysis;
  }

  // ── Findings invalidation (P2-5) ────────────────────────────────────────────
  // Signature of the placed plan (which cards sit where, with which rooms). A check snapshots it; if the
  // live signature later diverges, that check's findings are stale and the panel shows the re-run banner.
  get findingsPlanSignature(): string {
    const parts: string[] = [];
    for (const cardUuid of this.placedCardsSet) {
      const day = this.placedCardsDay.get(cardUuid) ?? '';
      const slots = (this.placedCardsTimeGridEntries.get(cardUuid) ?? []).join(',');
      const rooms = [...(this.cardRooms.get(cardUuid)?.keys() ?? [])].sort().join(',');
      parts.push(`${cardUuid}@${day}:${slots}#${rooms}`);
    }
    return parts.sort().join('|');
  }

  areFindingsStale(kind: 'control' | 'timefold' | 'test'): boolean {
    return this.uiStore.areFindingsStale(kind);
  }

  /** Class scope for a check: the selected class rows, or all class rows (mirrors the GridMenuBar handlers). */
  get checkClassUuids(): string[] {
    return this.context === 'classes' && this.selectedRows.length > 0
      ? this.selectedRows.map((row) => row.value)
      : this.getClassRows().map((row) => row.value);
  }

  /** Cards the next solver run may move, in the current class scope + keepExistingPositions option. */
  get solverChangeableCardCount(): number {
    return this.cardStore.countSolverChangeableCards(this.checkClassUuids);
  }

  /** Run-only (no mode toggle) control check — shared by the menu bar's first activation and the re-run banner. */
  async runControlCheck(): Promise<void> {
    const versionUuid = this.getCurrentVersion()?.uuid;
    if (!versionUuid) return;
    this.setControlModeLoading(true);
    const result = await controlTimetableVersion(versionUuid, this.checkClassUuids);
    this.setControlModeResult(result === undefined ? null : result);
    this.setControlModeLoading(false);
  }

  /** Run-only Timefold analyze — shared by the menu bar and the re-run banner. */
  async runTimefoldCheck(): Promise<void> {
    const versionUuid = this.getCurrentVersion()?.uuid;
    if (!versionUuid) return;
    const partialClassScope = Boolean(this.context === 'classes' && this.selectedRows.length > 0);
    this.setTimefoldAnalyzeModeLoading(true);
    try {
      const data = await analyzeTimefoldTimetableVersion(versionUuid, {
        classes: this.checkClassUuids,
        ...this.solverStore.generatorOptions,
      });
      if (data === undefined) {
        this.setTimefoldParsedAnalysis(null);
        void this.setTimefoldAnalyzeModal({
          result: null,
          error: i18next.t('pinboard.timefoldAnalyze.errorGeneric'),
          partialClassScope,
        });
      } else {
        this.setTimefoldParsedAnalysis(parseTimefoldScoreAnalysis(data));
        void this.setTimefoldAnalyzeModal({ result: data, error: null, partialClassScope });
      }
    } catch {
      this.setTimefoldParsedAnalysis(null);
      void this.setTimefoldAnalyzeModal({
        error: i18next.t('pinboard.timefoldAnalyze.errorGeneric'),
        partialClassScope,
      });
    }
    this.setTimefoldAnalyzeModeLoading(false);
  }

  /** Run-only test check (non-generator) — shared by the menu bar and the re-run banner. */
  async runTestCheck(): Promise<void> {
    const versionUuid = this.getCurrentVersion()?.uuid;
    if (!versionUuid) return;
    void this.setTestModalOpen(true);
    void this.setTestModalLoading(true);
    const result = await testTimetableVersion(versionUuid, this.checkClassUuids);
    void this.setTestModalResult(result);
    void this.setTestModalLoading(false);
  }

  setTimefoldAnalyzeModeActive(setActive: boolean) {
    return this.uiStore.setTimefoldAnalyzeModeActive(setActive);
  }
  setTimefoldAnalyzeModeLoading(isLoading: boolean) {
    return this.uiStore.setTimefoldAnalyzeModeLoading(isLoading);
  }
  expandTimefoldAnalyzeResults(expand: boolean) {
    return this.uiStore.expandTimefoldAnalyzeResults(expand);
  }

  async generate() {
    return this.uiStore.generate();
  }

  /** aSc-XML der aktuellen Version herunterladen (Klassen-Scope wie bei Test/Kontrolle). */
  async downloadAscExport(): Promise<boolean> {
    const versionUuid = this.getCurrentVersion()?.uuid;
    if (!versionUuid) {
      return false;
    }
    return downloadAscExport(versionUuid, this.checkClassUuids);
  }

  async downloadPdf() {
    return this.uiStore.downloadPdf();
  }
  setRowHeight(height: number) {
    return this.uiStore.setRowHeight(height);
  }
  setDefaultRowHeight() {
    return this.uiStore.setDefaultRowHeight();
  }
  setBoardLayout(layout: BoardLayout) {
    return this.uiStore.setBoardLayout(layout);
  }
  toggleBoardLayout() {
    return this.uiStore.toggleBoardLayout();
  }
  setBoardTopHeight(height: number) {
    return this.uiStore.setBoardTopHeight(height);
  }
  setBoardSplitWidth(width: number) {
    return this.uiStore.setBoardSplitWidth(width);
  }
  setBoardPickerWidth(width: number) {
    return this.uiStore.setBoardPickerWidth(width);
  }
  setBoardPickerHeight(height: number) {
    return this.uiStore.setBoardPickerHeight(height);
  }
  setShowPreviewMap(show: boolean) {
    return this.uiStore.setShowPreviewMap(show);
  }
  toggleShowPreviewMap() {
    return this.uiStore.toggleShowPreviewMap();
  }
  setPreviewMapContext(context: PreviewMapContext) {
    return this.uiStore.setPreviewMapContext(context);
  }
  setPreviewMapAuto(auto: boolean) {
    return this.uiStore.setPreviewMapAuto(auto);
  }
  setBoardPreviewMapWidth(width: number) {
    return this.uiStore.setBoardPreviewMapWidth(width);
  }
  setBoardPreviewMapHeight(height: number) {
    return this.uiStore.setBoardPreviewMapHeight(height);
  }
  setShowCardInfo(show: boolean) {
    return this.uiStore.setShowCardInfo(show);
  }
  toggleShowCardInfo() {
    return this.uiStore.toggleShowCardInfo();
  }
  setShowTimetablePreview(show: boolean) {
    return this.uiStore.setShowTimetablePreview(show);
  }
  toggleShowTimetablePreview() {
    return this.uiStore.toggleShowTimetablePreview();
  }
  setBoardTimetablePreviewTeachersWidth(width: number) {
    return this.uiStore.setBoardTimetablePreviewTeachersWidth(width);
  }
  setBoardTimetablePreviewTeachersHeight(height: number) {
    return this.uiStore.setBoardTimetablePreviewTeachersHeight(height);
  }
  setBoardTimetablePreviewRoomsWidth(width: number) {
    return this.uiStore.setBoardTimetablePreviewRoomsWidth(width);
  }
  setBoardTimetablePreviewRoomsHeight(height: number) {
    return this.uiStore.setBoardTimetablePreviewRoomsHeight(height);
  }
  setTimetablePreviewCellMinHeight(height: number) {
    return this.uiStore.setTimetablePreviewCellMinHeight(height);
  }
}
